import { Stack, Typography, useTheme } from "@mui/material";
import { type Control, useWatch } from "react-hook-form";
import { ClientPortfolioDomainEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { CoDebtorsSection } from "web_myplann/Portfolio/Forms/CoDebtors/CoDebtorsSection";
import {
	CONTRACT_REQUIRED_MINIMUM_FIELDS,
	getContractRequiredMinimumMessage,
} from "web_myplann/Portfolio/Forms/ContractShared/contractRequiredMinimum";
import type {
	LoansFormInput,
	LoansFormOutput,
} from "web_myplann/Portfolio/Forms/LoansForm/loansFormSchema";
import {
	hasLoanFixation,
	type LoanType,
} from "web_myplann/Portfolio/Forms/LoansForm/loanType";
import { useProducerProductOptions } from "web_myplann/Portfolio/Helpers/useProducerProductOptions";
import { useLoanPaymentFrequencyOptions } from "web_myplann/Portfolio/Hooks/useLoanPaymentFrequencyOptions";
import { RequiredMinimumMessage } from "web_myplann/Portfolio/PortfolioShared/RequiredMinimumMessage";
import { ExtendedParamsDisclosure } from "web_myplann/Shared/Components/ExtendedParamsDisclosure";
import { FlatRowFormCombobox } from "web_myplann/Shared/Components/FlatRow/FlatRowFormCombobox";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";

const PageResources = Resources.Portfolio.AddLoans;

type LoansFormFieldsProps = {
	control: Control<LoansFormInput, unknown, LoansFormOutput>;
	loanType?: LoanType;
	collapseParams?: boolean;
	showRemainsToPay?: boolean;
	namePlaceholder?: string;
	contractNumberPlaceholder?: string;
	showFixation?: boolean;
};

export const LoansFormFields = ({
	control,
	loanType,
	collapseParams = false,
	showRemainsToPay = false,
	namePlaceholder,
	contractNumberPlaceholder,
	showFixation = loanType ? hasLoanFixation(loanType) : false,
}: LoansFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();

	const {
		producerOptions,
		productOptions,
		isLoadingProducers,
		isLoadingProducts,
	} = useProducerProductOptions(control, ClientPortfolioDomainEnum.Loans);

	const paymentFrequencyOptions = useLoanPaymentFrequencyOptions();

	const signedAt = useWatch({ control, name: "signedAt" });

	return (
		<>
			<Typography variant="sectionLabel">
				{t(PageResources.Sections.basicInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="name"
					label={t(PageResources.Fields.name)}
					placeholder={
						namePlaceholder ??
						t(
							loanType
								? PageResources.NamePlaceholder[loanType]
								: Resources.Portfolio.AddShared.Placeholders.fill,
						)
					}
				/>
				<FlatRowFormInput
					control={control}
					name="contractNumber"
					label={t(PageResources.Fields.contractNumber)}
					placeholder={
						contractNumberPlaceholder ??
						t(Resources.Portfolio.AddShared.Placeholders.fill)
					}
				/>
				<FlatRowFormCombobox
					control={control}
					name="producer"
					label={t(PageResources.Fields.producer)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProducer,
					)}
					options={producerOptions}
					isLoading={isLoadingProducers}
				/>
				<FlatRowFormCombobox
					control={control}
					name="product"
					label={t(PageResources.Fields.product)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProduct,
					)}
					options={productOptions}
					isLoading={isLoadingProducts}
					isLast
				/>
			</Stack>
			<RequiredMinimumMessage
				control={control}
				fields={CONTRACT_REQUIRED_MINIMUM_FIELDS}
				message={getContractRequiredMinimumMessage(t, PageResources.Fields)}
			/>

			<Typography variant="sectionLabel">
				{t(PageResources.Sections.values)}
			</Typography>
			<ExtendedParamsDisclosure
				collapsible={collapseParams}
				showLabel={t(PageResources.showParameters)}
				hideLabel={t(PageResources.hideParameters)}
			>
				<Stack bgcolor={theme.palette.background.paper}>
					{showRemainsToPay && (
						<FlatRowFormInput
							control={control}
							name="value"
							type="float"
							max={ValidationLimits.Amount.Max}
							label={t(PageResources.Fields.remainsToPay)}
							placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
							suffix="Kč"
						/>
					)}
					<FlatRowFormInput
						control={control}
						name="change.originalObligationAmount"
						type="float"
						max={ValidationLimits.Amount.Max}
						label={t(PageResources.Fields.originalObligationAmount)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
						suffix="Kč"
					/>
					<FlatRowFormInput
						control={control}
						name="change.installment"
						type="float"
						max={ValidationLimits.Amount.Max}
						label={t(PageResources.Fields.installment)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
						suffix="Kč"
					/>
					<FlatRowFormSelect
						control={control}
						name="change.paymentFrequency"
						label={t(PageResources.Fields.paymentFrequency)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
						options={paymentFrequencyOptions}
					/>
					<FlatRowFormInput
						control={control}
						name="change.interestRate"
						type="float"
						max={ValidationLimits.Amount.MaxInterestRate}
						label={t(PageResources.Fields.interestRate)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
						suffix="% p.a."
						isLast={!showFixation}
					/>
					{showFixation && (
						<FlatRowFormDatePicker
							control={control}
							name="change.fixationEndDate"
							label={t(PageResources.Fields.fixationEndDate)}
							placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
							views={[DateView.Year, DateView.Month]}
							displayFormat={DateFormat.MonthYearLong}
							isLast
						/>
					)}
				</Stack>
			</ExtendedParamsDisclosure>

			<CoDebtorsSection
				control={control}
				sectionLabel={t(PageResources.Sections.coDebtors)}
			/>

			<Typography variant="sectionLabel">
				{t(PageResources.Sections.additionalInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormDatePicker
					control={control}
					name="signedAt"
					label={t(PageResources.Fields.signedAt)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="startAt"
					label={t(PageResources.Fields.startAt)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="endAt"
					label={t(PageResources.Fields.endAt)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					minDate={signedAt ?? undefined}
					displayFormat={DateFormat.Display}
					isLast
				/>
			</Stack>
		</>
	);
};
